import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

import {
  obtenerContextoVigente,
  publicarContextoDeListado,
} from '../../../nucleo/navegacion/contextoDeNavegacion.js';

/*
 * LA CAPTURA LIMPIA DEL BOTÓN «+» · UNA SOLA IMPLEMENTACIÓN PARA LOS CUATRO TIPOS
 * (FIX de ingresos, punto 1: RN-FIX-M01 / CA-FIX-M01 con D-478, y D-479).
 *
 * EL DEFECTO, TAL COMO LO VIO DEISY PAEZ EN EL TELÉFONO: tras «Ingreso guardado»,
 * tocar «+» y elegir «Ingreso» no abría «Nuevo ingreso»; la pantalla se quedaba en
 * el aviso de guardado y solo una recarga del navegador devolvía el formulario.
 *
 * LA CAUSA, CONFIRMADA EN EL CÓDIGO: el resumen de guardado vive en la MISMA RUTA
 * que el formulario (RN-394, `CapturaDeMovimiento.jsx`: con `resultado` se pinta el
 * resumen y sin él, el formulario). El menú del «+» navega a la ruta en la que ya
 * se está, así que el enrutador no cambia de ruta, no re-monta la pantalla y el
 * estado `resultado` sigue ahí: el formulario nunca vuelve a arrancar. La recarga
 * «funcionaba» porque tiraba todo el estado del navegador.
 *
 * QUÉ HACE ESTA PIEZA, y por qué así:
 *
 *   1. `conCapturaLimpia` monta la pantalla con el PASO DE NAVEGACIÓN como llave.
 *      Dos visitas distintas a la misma ruta son dos pasos distintos —`location.key`
 *      cambia aunque la dirección sea idéntica—, y una llave nueva hace que React
 *      desmonte y vuelva a montar la pantalla. Es la forma de dejar el formulario
 *      TOTALMENTE VACÍO que exige D-479 sin ir campo por campo: se reinicia TODO el
 *      estado de la pantalla —importe, categoría, cuenta(s), fecha de hoy, nota, el
 *      aviso de guardado y el propio `resultado`— y se vuelven a pedir los
 *      catálogos, igual que en una apertura normal. Reiniciar «los campos que me
 *      acuerde» es justo lo que deja viva la mitad del defecto.
 *
 *      NO conserva la cuenta del movimiento recién guardado (D-479, palabras
 *      literales: «abre un formulario LIMPIO siempre»), y tampoco la categoría: el
 *      menú no manda sugerencias desde el resumen (ver el punto 2).
 *
 *      LO QUE NO CAMBIA: tocar «+» sin elegir nada sigue sin navegar y sin tocar la
 *      pila (D-244) —eso lo decide el armazón y aquí no se toca—, y una apertura
 *      corriente desde el historial se comporta exactamente como hoy, porque llegar
 *      por primera vez a una ruta ya era un montaje nuevo.
 *
 *   2. `useResumenCedeElPasoAlMenu` publica, MIENTRAS EL RESUMEN ESTÁ A LA VISTA,
 *      el destino de regreso que el resumen traía, para que la captura que abra el
 *      menú lo herede (RN-FIX-M01(b)). Sin esto el menú abre la ruta pelada —el
 *      armazón solo hereda el contexto cuando hay un listado a la vista (RN-411)— y
 *      la flecha de la captura nueva aterrizaría en `/movimientos` sin el filtro,
 *      en vez de en la lista con el filtro con el que se empezó (CA-FIX-M01 paso 4).
 *      Se publica con la pieza del núcleo que ya existe para eso, sin tocarla.
 *
 *   3. `reemplazarElPaso: true` viaja en ese mismo contexto y CIERRA D-478: la
 *      captura que abre el menú REEMPLAZA el paso del resumen en vez de apilarse
 *      encima, así que el atrás del teléfono deja en el destino de regreso original
 *      de un solo toque, también al cambiar de tipo (CA-FIX-M01 paso 5). Lo lee el
 *      armazón (`componentes/Shell.jsx`, `rutaDeLaOpcion`) y lo aplica el menú de
 *      tipos (`nucleo/navegacion/MenuDeTiposDeMovimiento.jsx`), con autorización
 *      expresa de Deisy Paez para esos dos archivos del núcleo y nada más.
 *
 *      EL SÍNTOMA REAL, MEDIDO y no supuesto (instrumentación de `history.length`
 *      del Tester): el aviso de guardado NUNCA reaparecía con el atrás —cualquier
 *      paso de historial remonta la pantalla con `resultado = null`, en cualquier
 *      dirección—, y elegir el MISMO tipo otra vez tampoco agregaba paso. El único
 *      caso defectuoso era elegir OTRO tipo desde el resumen (Ingreso → Gasto): el
 *      atrás aterrizaba en un formulario limpio FANTASMA del tipo anterior y hacía
 *      falta un segundo toque. Ese es el caso que cierra el reemplazo. La redacción
 *      original de D-478 describía además un síntoma —el guardado alcanzable con el
 *      atrás— que la medición demostró inexistente.
 *
 *      Y NO ROMPE NADA DE ALREDEDOR: el reemplazo solo se pide mientras el resumen
 *      está a la vista, así que el «+» de un listado sigue empujando su paso como
 *      siempre (RN-411), tocar «+» sin elegir nada sigue sin tocar la pila (D-244) y
 *      el remontaje de D-479 se conserva, porque un reemplazo también estrena
 *      `location.key`.
 */

/**
 * Envuelve una pantalla de captura para que cada paso de navegación la monte
 * limpia (D-479). Se aplica UNA VEZ por pantalla y cubre los cuatro tipos: gasto,
 * ingreso y devolución comparten `CapturaDeMovimiento`, y el traspaso es
 * `CapturaDeTraspaso`.
 */
export function conCapturaLimpia(Captura) {
  function CapturaConPasoPropio(propiedades) {
    const { key: pasoDeNavegacion } = useLocation();

    // `key` vale 'default' en el primer paso de la sesión del navegador; se usa
    // tal cual, que es una llave estable como cualquier otra.
    return <Captura key={pasoDeNavegacion ?? 'default'} {...propiedades} />;
  }

  CapturaConPasoPropio.displayName = `conCapturaLimpia(${Captura.displayName ?? Captura.name ?? 'Captura'})`;

  return CapturaConPasoPropio;
}

/**
 * Mientras el resumen de guardado está a la vista, el menú del «+» hereda de él el
 * destino de regreso (RN-FIX-M01(b)).
 *
 * @param destinoDeRegreso  la dirección exacta a la que vuelve el resumen.
 * @param estaEnElResumen   verdadero solo con el aviso de guardado en pantalla.
 */
export function useResumenCedeElPasoAlMenu(destinoDeRegreso, estaEnElResumen) {
  useEffect(() => {
    if (!estaEnElResumen || !destinoDeRegreso) return undefined;

    /*
     * SIN SUGERENCIAS, a propósito: el formulario llega vacío también de categoría
     * (D-479). `reemplazarElPaso` es lo que hace que la captura nueva ocupe el paso
     * del resumen en vez de apilarse encima (D-478); lo lee el armazón.
     */
    const publicado = { direccion: destinoDeRegreso, sugerencias: null, reemplazarElPaso: true };
    publicarContextoDeListado(publicado);

    /*
     * SE RETIRA SOLO SI SIGUE SIENDO EL NUESTRO, y por identidad del objeto, no por
     * dirección: al salir del resumen hacia el historial, la lista publica su propio
     * contexto EN SU RENDER —antes de que corra esta limpieza— y su dirección es la
     * MISMA que la nuestra. Comparando direcciones le borraríamos a la lista el
     * contexto que acaba de publicar y el «+» del historial perdería el filtro
     * (RN-411), que es una regresión y no un efecto de este FIX.
     */
    return () => {
      if (obtenerContextoVigente() === publicado) publicarContextoDeListado(null);
    };
  }, [destinoDeRegreso, estaEnElResumen]);
}

export default conCapturaLimpia;
